import { useEffect, useMemo, useState } from "react";
import {
  Plus,
  Trash2,
  Pencil,
  LineChart as LineChartIcon,
  Eye,
  EyeOff,
  TrendingUp,
  TrendingDown,
  Minus,
  ImageIcon,
  Upload,
  Printer,
} from "lucide-react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import { toast } from "sonner";
import PageContainer from "@/components/PageContainer";
import CattlePrintReport from "@/components/CattlePrintReport";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useConfig } from "@/context/ConfigContext";
import { compressImage } from "@/lib/image";
import { fmtRupiah, fmt } from "@/lib/feedModels";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const STATUSES = ["Tersedia", "Booking", "Terjual"];
const STATUS_BADGE = {
  Tersedia: "bg-emerald-100 text-emerald-800 border-emerald-200",
  Booking: "bg-[#FBEAD6] text-[#B87148] border-[#D38B60]",
  Terjual: "bg-slate-200 text-slate-600 border-slate-300",
};
const EMPTY_FORM = { name: "", cattle_number: "", buy_price: "", status: "Tersedia", zone: "", photo: null };

function firstLastDiff(weights) {
  if (!weights || weights.length < 1) return null;
  const first = weights[0].weight;
  const last = weights[weights.length - 1].weight;
  return { first, last, diff: last - first };
}

function getTrend(weights) {
  if (!weights || weights.length === 0) return { latest: null, diff: null };
  const latest = weights[weights.length - 1].weight;
  if (weights.length < 2) return { latest, diff: null };
  const prev = weights[weights.length - 2].weight;
  return { latest, diff: latest - prev };
}

function TrendIndicator({ diff }) {
  if (diff === null || diff === undefined)
    return <span className="text-xs text-[#C99A6A]">—</span>;
  if (diff > 0)
    return (
      <span className="inline-flex items-center gap-1 text-emerald-600 text-xs font-semibold">
        <TrendingUp className="w-3.5 h-3.5" /> +{fmt(diff)} kg
      </span>
    );
  if (diff < 0)
    return (
      <span className="inline-flex items-center gap-1 text-red-500 text-xs font-semibold">
        <TrendingDown className="w-3.5 h-3.5" /> {fmt(diff)} kg
      </span>
    );
  return (
    <span className="inline-flex items-center gap-1 text-slate-400 text-xs font-semibold">
      <Minus className="w-3.5 h-3.5" /> 0 kg
    </span>
  );
}

export default function DataSapiPage() {
  const { user } = useAuth();
  const { config } = useConfig();
  const zones = config.zones || [];
  const isAdmin = user?.role === "admin";

  const [cattle, setCattle] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPrice, setShowPrice] = useState(false);
  const [zoneFilter, setZoneFilter] = useState("all");
  const [selected, setSelected] = useState({});

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [weightOpen, setWeightOpen] = useState(false);
  const [activeCattle, setActiveCattle] = useState(null);
  const [weightForm, setWeightForm] = useState({ month: "", weight: "" });
  const [editWeightIdx, setEditWeightIdx] = useState(null);
  const [editWeightForm, setEditWeightForm] = useState({ month: "", weight: "" });
  const [confirm, setConfirm] = useState(null);
  const [printCattle, setPrintCattle] = useState(null);

  const fetchCattle = async () => {
    try {
      const res = await api.get("/cattle");
      setCattle(res.data);
    } catch (e) {
      toast.error("Gagal memuat data sapi");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCattle();
  }, []);

  const openAdd = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setFormOpen(true);
  };

  const openEdit = (c) => {
    setEditing(c);
    setForm({
      name: c.name,
      cattle_number: c.cattle_number,
      buy_price: c.buy_price ?? "",
      status: c.status,
      zone: c.zone || "",
      photo: c.photo || null,
    });
    setFormOpen(true);
  };

  const handlePhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const dataUrl = await compressImage(file);
      setForm((f) => ({ ...f, photo: dataUrl }));
    } catch (err) {
      toast.error("Gagal memproses foto");
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        cattle_number: form.cattle_number.trim(),
        buy_price: parseFloat(form.buy_price) || 0,
        status: form.status,
        zone: form.zone || null,
        photo: form.photo,
      };
      if (editing) {
        const res = await api.put(`/cattle/${editing.id}`, payload);
        setCattle((prev) => prev.map((c) => (c.id === editing.id ? res.data : c)));
        toast.success("Data sapi diperbarui");
      } else {
        const res = await api.post("/cattle", payload);
        setCattle((prev) => [res.data, ...prev]);
        toast.success("Sapi berhasil ditambahkan");
      }
      setFormOpen(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (c, status) => {
    try {
      const res = await api.put(`/cattle/${c.id}`, { status });
      setCattle((prev) => prev.map((x) => (x.id === c.id ? res.data : x)));
      toast.success(`Status ${c.name} → ${status}`);
    } catch (e) {
      toast.error("Gagal mengubah status");
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/cattle/${id}`);
      setCattle((prev) => prev.filter((c) => c.id !== id));
      toast.success("Data sapi dihapus");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const openWeight = (c) => {
    setActiveCattle(c);
    setWeightForm({ month: "", weight: "" });
    setWeightOpen(true);
  };

  const handleAddWeight = async () => {
    if (!weightForm.month.trim() || !weightForm.weight) {
      toast.error("Isi bulan dan bobot");
      return;
    }
    try {
      const res = await api.post(`/cattle/${activeCattle.id}/weights`, {
        month: weightForm.month.trim(),
        weight: parseFloat(weightForm.weight),
      });
      setCattle((prev) => prev.map((c) => (c.id === activeCattle.id ? res.data : c)));
      setActiveCattle(res.data);
      setWeightForm({ month: "", weight: "" });
      toast.success("Bobot ditambahkan");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const handleDeleteWeight = async (index) => {
    try {
      const res = await api.delete(`/cattle/${activeCattle.id}/weights/${index}`);
      setCattle((prev) => prev.map((c) => (c.id === activeCattle.id ? res.data : c)));
      setActiveCattle(res.data);
      toast.success("Data bobot dihapus");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const startEditWeight = (index, w) => {
    setEditWeightIdx(index);
    setEditWeightForm({ month: w.month, weight: String(w.weight) });
  };

  const handleSaveEditWeight = async () => {
    if (!editWeightForm.month.trim() || !editWeightForm.weight) {
      toast.error("Isi bulan dan bobot");
      return;
    }
    try {
      const res = await api.put(`/cattle/${activeCattle.id}/weights/${editWeightIdx}`, {
        month: editWeightForm.month.trim(),
        weight: parseFloat(editWeightForm.weight),
      });
      setCattle((prev) => prev.map((c) => (c.id === activeCattle.id ? res.data : c)));
      setActiveCattle(res.data);
      setEditWeightIdx(null);
      toast.success("Bobot berhasil diperbarui");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const handlePrintCattle = (c) => {
    setPrintCattle(c);
    setTimeout(() => window.print(), 150);
  };

  const chartData = useMemo(
    () => (activeCattle?.weights || []).map((w) => ({ month: w.month, bobot: w.weight })),
    [activeCattle]
  );

  const anySelected = Object.values(selected).some(Boolean);
  const summaryCattle = anySelected
    ? cattle.filter((c) => selected[c.id])
    : zoneFilter === "all"
    ? cattle
    : cattle.filter((c) => (c.zone || "") === zoneFilter);
  const summaryTotalWeight = summaryCattle.reduce((s, c) => {
    const t = getTrend(c.weights);
    return s + (t.latest || 0);
  }, 0);

  return (
    <PageContainer
      title="Data Sapi"
      subtitle="Kelola data ternak, bobot, status, dan foto"
      action={
        <div className="flex items-center gap-2">
          {isAdmin && (
            <Button
              data-testid="btn-toggle-price"
              variant="outline"
              onClick={() => setShowPrice((v) => !v)}
              className="rounded-full border-[#E0A988]/70 text-[#A06E4D] hover:bg-[#F7E5D2]"
            >
              {showPrice ? <EyeOff className="w-4 h-4 mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
              {showPrice ? "Sembunyikan Harga" : "Tampilkan Harga"}
            </Button>
          )}
          {isAdmin && (
            <Button
              data-testid="btn-open-add-cattle"
              onClick={openAdd}
              className="rounded-full bg-[#A06E4D] hover:bg-[#8A5230] text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              Tambah Sapi
            </Button>
          )}
        </div>
      }
    >
      <div data-testid="weight-summary" className="bg-white rounded-2xl shadow-sm border border-[#E0A988]/40 p-5 mb-6 flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="flex items-center gap-2">
          <Label className="text-sm font-semibold text-[#7A4A28] whitespace-nowrap">Ringkasan Bobot —</Label>
          <Select value={zoneFilter} onValueChange={(v) => { setZoneFilter(v); setSelected({}); }}>
            <SelectTrigger data-testid="summary-zone-filter" className="h-9 w-44 bg-[#FBF3E8] border-[#E0A988]/50">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua Zona</SelectItem>
              {zones.map((z) => (
                <SelectItem key={z} value={z} data-testid={`summary-zone-${z}`}>{z}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1 grid grid-cols-2 gap-3">
          <div className="rounded-lg bg-[#FBEAD6] border border-[#E0A988]/50 px-4 py-2">
            <p className="text-[10px] uppercase tracking-wider text-[#A06E4D] font-semibold">Jumlah Sapi</p>
            <p data-testid="summary-count" className="text-xl font-mono-num font-bold text-[#7A4A28]">{summaryCattle.length}</p>
          </div>
          <div className="rounded-lg bg-[#8A5230] text-white px-4 py-2">
            <p className="text-[10px] uppercase tracking-wider text-[#F7E5D2] font-semibold">Total Bobot (timbangan terakhir)</p>
            <p data-testid="summary-total-weight" className="text-xl font-mono-num font-bold">{fmt(summaryTotalWeight)} kg</p>
          </div>
        </div>
        {anySelected && (
          <p className="text-xs text-[#A06E4D]">Menghitung {summaryCattle.length} sapi terpilih (centang di tabel)</p>
        )}
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-[#E0A988]/40 p-6">
        {loading ? (
          <p className="text-sm text-[#C99A6A] py-6 text-center">Memuat...</p>
        ) : cattle.length === 0 ? (
          <div
            data-testid="cattle-empty"
            className="rounded-xl border border-dashed border-[#E0A988]/60 bg-[#FBF3E8] p-10 text-center text-sm text-[#C99A6A]"
          >
            Belum ada data sapi.
          </div>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-[#F0E3D2]">
            <Table data-testid="cattle-table">
              <TableHeader>
                <TableRow className="bg-[#FBF3E8] [&>th]:text-center">
                  <TableHead className="text-xs font-semibold w-8 text-center"></TableHead>
                  <TableHead className="text-xs font-semibold w-12 text-center">No</TableHead>
                  <TableHead className="text-xs font-semibold text-center">Foto</TableHead>
                  <TableHead className="text-xs font-semibold text-center">Nama Sapi</TableHead>
                  <TableHead className="text-xs font-semibold text-center">Nomor Sapi</TableHead>
                  <TableHead className="text-xs font-semibold text-center">Zona</TableHead>
                  {isAdmin && (
                    <TableHead className="text-xs font-semibold text-center">Harga Beli</TableHead>
                  )}
                  <TableHead className="text-xs font-semibold text-center">Bobot Terkini</TableHead>
                  <TableHead className="text-xs font-semibold text-center">Perubahan</TableHead>
                  <TableHead className="text-xs font-semibold text-center">Selisih Awal→Akhir</TableHead>
                  <TableHead className="text-xs font-semibold text-center">Status</TableHead>
                  <TableHead className="text-xs font-semibold text-center">Aksi</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {cattle.map((c, idx) => {
                  const { latest, diff } = getTrend(c.weights);
                  const fl = firstLastDiff(c.weights);
                  return (
                    <TableRow key={c.id} data-testid={`cattle-row-${c.id}`} className="[&>td]:text-center [&>td]:align-middle">
                      <TableCell>
                        <input
                          type="checkbox"
                          data-testid={`cattle-check-${c.id}`}
                          checked={!!selected[c.id]}
                          onChange={(e) => setSelected((p) => ({ ...p, [c.id]: e.target.checked }))}
                          className="w-4 h-4 accent-[#A06E4D]"
                        />
                      </TableCell>
                      <TableCell className="text-sm text-[#A06E4D]">{idx + 1}</TableCell>
                      <TableCell>
                        {c.photo ? (
                          <img
                            src={c.photo}
                            alt={c.name}
                            className="w-12 h-12 rounded-lg object-cover border border-[#E0A988]/40 mx-auto"
                          />
                        ) : (
                          <div className="w-12 h-12 rounded-lg bg-[#F7E5D2] flex items-center justify-center mx-auto">
                            <ImageIcon className="w-5 h-5 text-[#C99A6A]" />
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="font-medium text-[#7A4A28]">{c.name}</TableCell>
                      <TableCell className="text-sm font-mono-num text-[#7A4A28]">
                        {c.cattle_number}
                      </TableCell>
                      <TableCell className="text-sm text-[#7A4A28]">
                        {c.zone || <span className="text-[#C99A6A]">—</span>}
                      </TableCell>
                      {isAdmin && (
                        <TableCell
                          data-testid={`cattle-price-${c.id}`}
                          className="font-mono-num text-sm text-[#8A5230]"
                        >
                          {showPrice ? fmtRupiah(c.buy_price) : "••••••"}
                        </TableCell>
                      )}
                      <TableCell className="font-mono-num text-sm font-bold text-[#7A4A28]">
                        {latest !== null ? `${fmt(latest)} kg` : "—"}
                      </TableCell>
                      <TableCell>
                        <TrendIndicator diff={diff} />
                      </TableCell>
                      <TableCell data-testid={`cattle-selisih-${c.id}`} className="text-sm font-mono-num">
                        {fl && fl.diff !== null ? (
                          <span className={fl.diff > 0 ? "text-emerald-600 font-semibold" : fl.diff < 0 ? "text-red-500 font-semibold" : "text-slate-400"}>
                            {fl.diff > 0 ? "+" : ""}{fmt(fl.diff)} kg
                          </span>
                        ) : (
                          <span className="text-[#C99A6A]">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {isAdmin ? (
                          <Select value={c.status} onValueChange={(v) => handleStatusChange(c, v)}>
                            <SelectTrigger
                              data-testid={`select-status-${c.id}`}
                              className="h-8 w-32 text-xs bg-white border-[#E0A988]/50 mx-auto"
                            >
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {STATUSES.map((s) => (
                                <SelectItem key={s} value={s} data-testid={`status-option-${c.id}-${s}`}>
                                  {s}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <Badge variant="outline" className={`${STATUS_BADGE[c.status]} font-medium`}>
                            {c.status}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex justify-center gap-1">
                          <Button
                            data-testid={`btn-weight-${c.id}`}
                            variant="ghost"
                            size="icon"
                            onClick={() => openWeight(c)}
                            className="h-8 w-8 text-[#A06E4D] hover:text-[#8A5230] hover:bg-[#F7E5D2]"
                          >
                            <LineChartIcon className="w-4 h-4" />
                          </Button>
                          {isAdmin && (
                            <>
                              <Button
                                data-testid={`btn-edit-cattle-${c.id}`}
                                variant="ghost"
                                size="icon"
                                onClick={() => openEdit(c)}
                                className="h-8 w-8 text-[#A06E4D] hover:text-[#8A5230] hover:bg-[#F7E5D2]"
                              >
                                <Pencil className="w-4 h-4" />
                              </Button>
                              <Button
                                data-testid={`btn-delete-cattle-${c.id}`}
                                variant="ghost"
                                size="icon"
                                onClick={() =>
                                  setConfirm({
                                    title: "Hapus Data Sapi?",
                                    desc: `Data sapi "${c.name}" (${c.cattle_number}) beserta seluruh riwayat bobotnya akan dihapus permanen.`,
                                    onConfirm: () => handleDelete(c.id),
                                  })
                                }
                                className="h-8 w-8 text-slate-500 hover:text-red-600 hover:bg-red-50"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* Add / Edit dialog */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent data-testid="cattle-form-dialog" className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-[#8A5230]">
              {editing ? "Edit Data Sapi" : "Tambah Sapi Baru"}
            </DialogTitle>
            <DialogDescription className="text-[#A06E4D]">
              Lengkapi informasi sapi beserta foto dan harga beli.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="flex items-center gap-4">
              <div className="w-24 h-24 rounded-xl bg-[#F7E5D2] flex items-center justify-center overflow-hidden border border-[#E0A988]/40 shrink-0">
                {form.photo ? (
                  <img src={form.photo} alt="preview" className="w-full h-full object-cover" />
                ) : (
                  <ImageIcon className="w-8 h-8 text-[#C99A6A]" />
                )}
              </div>
              <div>
                <Label
                  htmlFor="cattle-photo"
                  className="inline-flex items-center gap-2 cursor-pointer text-sm font-semibold text-[#A06E4D] bg-[#F7E5D2] hover:bg-[#F0D9BF] rounded-full px-4 py-2"
                >
                  <Upload className="w-4 h-4" />
                  {uploading ? "Memproses..." : "Upload Foto"}
                </Label>
                <input
                  id="cattle-photo"
                  data-testid="input-cattle-photo"
                  type="file"
                  accept="image/*"
                  onChange={handlePhoto}
                  className="hidden"
                />
                <p className="text-xs text-[#C99A6A] mt-1">Satu foto per sapi (JPG/PNG)</p>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-semibold text-[#7A4A28]">Nama Sapi</Label>
              <Input
                data-testid="input-cattle-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="bg-[#FBF3E8] border-[#E0A988]/60"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-semibold text-[#7A4A28]">Nomor Sapi</Label>
              <Input
                data-testid="input-cattle-number"
                value={form.cattle_number}
                onChange={(e) => setForm({ ...form, cattle_number: e.target.value })}
                className="bg-[#FBF3E8] border-[#E0A988]/60"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-semibold text-[#7A4A28]">Harga Beli (Rp)</Label>
              <Input
                data-testid="input-cattle-price"
                type="number"
                min="0"
                value={form.buy_price}
                onChange={(e) => setForm({ ...form, buy_price: e.target.value })}
                className="bg-[#FBF3E8] border-[#E0A988]/60 font-mono-num"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-semibold text-[#7A4A28]">Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="select-cattle-status" className="bg-[#FBF3E8] border-[#E0A988]/60">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUSES.map((s) => (
                    <SelectItem key={s} value={s} data-testid={`form-status-option-${s}`}>
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-semibold text-[#7A4A28]">Zona / Kandang</Label>
              <Select value={form.zone || "none"} onValueChange={(v) => setForm({ ...form, zone: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="select-cattle-zone" className="bg-[#FBF3E8] border-[#E0A988]/60">
                  <SelectValue placeholder="Pilih zona" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Tanpa Zona —</SelectItem>
                  {zones.map((z) => (
                    <SelectItem key={z} value={z} data-testid={`cattle-zone-option-${z}`}>{z}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button
              data-testid="btn-submit-cattle"
              onClick={handleSave}
              disabled={saving || !form.name || !form.cattle_number}
              className="rounded-full bg-[#A06E4D] hover:bg-[#8A5230] text-white"
            >
              {saving ? "Menyimpan..." : "Simpan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Weight tracking dialog */}
      <Dialog open={weightOpen} onOpenChange={setWeightOpen}>
        <DialogContent data-testid="weight-dialog" className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-[#8A5230]">
              Riwayat Bobot — {activeCattle?.name}
            </DialogTitle>
            <DialogDescription className="text-[#A06E4D]">
              Grafik tren dan riwayat penimbangan bobot sapi per periode.
            </DialogDescription>
          </DialogHeader>

          <div className="flex justify-end -mt-2">
            <Button
              data-testid="btn-print-cattle"
              variant="outline"
              size="sm"
              disabled={!activeCattle?.weights?.length}
              onClick={() => handlePrintCattle(activeCattle)}
              className="rounded-full border-[#E0A988]/70 text-[#A06E4D] hover:bg-[#F7E5D2]"
            >
              <Printer className="w-4 h-4 mr-2" /> Cetak Laporan
            </Button>
          </div>

          <div className="space-y-5 py-2">
            {chartData.length > 0 ? (
              <div className="h-56 w-full bg-[#FBF3E8] rounded-xl p-3 border border-[#E0A988]/40">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData} margin={{ top: 8, right: 12, left: -12, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E8D5BF" />
                    <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#A06E4D" }} />
                    <YAxis tick={{ fontSize: 11, fill: "#A06E4D" }} />
                    <Tooltip
                      contentStyle={{
                        borderRadius: 12,
                        border: "1px solid #E0A988",
                        fontSize: 12,
                      }}
                      formatter={(v) => [`${fmt(v)} kg`, "Bobot"]}
                    />
                    <Line
                      type="monotone"
                      dataKey="bobot"
                      stroke="#A06E4D"
                      strokeWidth={2.5}
                      dot={{ r: 4, fill: "#8A5230" }}
                      activeDot={{ r: 6 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <p className="text-sm text-[#C99A6A] text-center py-6">
                Belum ada data bobot. {isAdmin ? "Tambahkan di bawah." : ""}
              </p>
            )}

            {(activeCattle?.weights || []).length > 0 && (
              <div className="overflow-x-auto rounded-xl border border-[#F0E3D2]">
                <Table data-testid="weight-table">
                  <TableHeader>
                    <TableRow className="bg-[#FBF3E8] [&>th]:text-center">
                      <TableHead className="text-xs font-semibold text-center">Bulan</TableHead>
                      <TableHead className="text-xs font-semibold text-center">Bobot</TableHead>
                      <TableHead className="text-xs font-semibold text-center">Perubahan</TableHead>
                      {isAdmin && <TableHead className="text-xs font-semibold text-center">Aksi</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {activeCattle.weights.map((w, i) => {
                      const prev = i > 0 ? activeCattle.weights[i - 1].weight : null;
                      const d = prev === null ? null : w.weight - prev;
                      const isEditing = editWeightIdx === i;
                      return (
                        <TableRow key={i} className="[&>td]:text-center [&>td]:align-middle">
                          {isEditing ? (
                            <>
                              <TableCell>
                                <Input
                                  data-testid={`edit-weight-month-${i}`}
                                  value={editWeightForm.month}
                                  onChange={(e) => setEditWeightForm({ ...editWeightForm, month: e.target.value })}
                                  className="h-9 bg-white border-[#E0A988]/60 text-sm"
                                />
                              </TableCell>
                              <TableCell>
                                <Input
                                  data-testid={`edit-weight-value-${i}`}
                                  type="number"
                                  value={editWeightForm.weight}
                                  onChange={(e) => setEditWeightForm({ ...editWeightForm, weight: e.target.value })}
                                  className="h-9 bg-white border-[#E0A988]/60 text-sm font-mono-num"
                                />
                              </TableCell>
                              <TableCell colSpan={2}>
                                <div className="flex justify-center gap-2">
                                  <Button
                                    data-testid={`btn-save-weight-${i}`}
                                    size="sm"
                                    onClick={handleSaveEditWeight}
                                    className="h-8 rounded-full bg-[#A06E4D] hover:bg-[#8A5230] text-white"
                                  >
                                    Simpan
                                  </Button>
                                  <Button
                                    data-testid={`btn-cancel-weight-${i}`}
                                    size="sm"
                                    variant="outline"
                                    onClick={() => setEditWeightIdx(null)}
                                    className="h-8 rounded-full border-[#E0A988]/70 text-[#A06E4D]"
                                  >
                                    Batal
                                  </Button>
                                </div>
                              </TableCell>
                            </>
                          ) : (
                            <>
                              <TableCell className="text-sm text-[#7A4A28]">{w.month}</TableCell>
                              <TableCell className="font-mono-num text-sm font-bold text-[#7A4A28]">
                                {fmt(w.weight)} kg
                              </TableCell>
                              <TableCell>
                                <TrendIndicator diff={d} />
                              </TableCell>
                              {isAdmin && (
                                <TableCell>
                                  <div className="flex justify-center gap-1">
                                    <Button
                                      data-testid={`btn-edit-weight-${i}`}
                                      variant="ghost"
                                      size="icon"
                                      onClick={() => startEditWeight(i, w)}
                                      className="h-8 w-8 text-[#A06E4D] hover:text-[#8A5230] hover:bg-[#F7E5D2]"
                                    >
                                      <Pencil className="w-4 h-4" />
                                    </Button>
                                    <Button
                                      data-testid={`btn-delete-weight-${i}`}
                                      variant="ghost"
                                      size="icon"
                                      onClick={() =>
                                        setConfirm({
                                          title: "Hapus Data Bobot?",
                                          desc: `Catatan bobot "${w.month}" (${fmt(w.weight)} kg) akan dihapus.`,
                                          onConfirm: () => handleDeleteWeight(i),
                                        })
                                      }
                                      className="h-8 w-8 text-slate-500 hover:text-red-600 hover:bg-red-50"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </div>
                                </TableCell>
                              )}
                            </>
                          )}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}

            {isAdmin && (
              <div className="flex flex-col sm:flex-row items-end gap-3 bg-[#FBF3E8] p-4 rounded-xl border border-[#E0A988]/40">
                <div className="flex-1 space-y-1 w-full">
                  <Label className="text-xs font-semibold text-[#7A4A28]">Bulan / Periode</Label>
                  <Input
                    data-testid="input-weight-month"
                    placeholder="Contoh: Jan 2026"
                    value={weightForm.month}
                    onChange={(e) => setWeightForm({ ...weightForm, month: e.target.value })}
                    className="bg-white border-[#E0A988]/60 h-10"
                  />
                </div>
                <div className="flex-1 space-y-1 w-full">
                  <Label className="text-xs font-semibold text-[#7A4A28]">Bobot (kg)</Label>
                  <Input
                    data-testid="input-weight-value"
                    type="number"
                    min="0"
                    placeholder="0"
                    value={weightForm.weight}
                    onChange={(e) => setWeightForm({ ...weightForm, weight: e.target.value })}
                    className="bg-white border-[#E0A988]/60 h-10 font-mono-num"
                  />
                </div>
                <Button
                  data-testid="btn-add-weight"
                  onClick={handleAddWeight}
                  className="rounded-full bg-[#A06E4D] hover:bg-[#8A5230] text-white h-10"
                >
                  <Plus className="w-4 h-4 mr-1" /> Tambah
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent data-testid="confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-[#8A5230]">
              {confirm?.title}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#7A4A28]">
              {confirm?.desc}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="btn-confirm-cancel" className="rounded-full">
              Batal
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="btn-confirm-delete"
              onClick={() => {
                confirm?.onConfirm?.();
                setConfirm(null);
              }}
              className="rounded-full bg-red-600 hover:bg-red-700 text-white"
            >
              Hapus
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <CattlePrintReport cattle={printCattle} showPrice={isAdmin && showPrice} />
    </PageContainer>
  );
}
